import type * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useLocation } from 'react-router-dom'

import { formatDistanceToNowStrict } from 'date-fns'

import {
  Button,
  Card,
  DEFAULT_ICON_PICKER_VALUE,
  Dialog,
  Flex,
  IconButton,
  IconPicker,
  Input,
  Label,
  Notice,
  Text,
  TrashIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { IconPickerValue } from '@buffer-mono/popcorn'

import {
  captureFilters,
  deriveSectionFromRoute,
  deriveViewFromRoute,
} from '~publish/helpers/savedViews'
import { isOfType } from '~publish/helpers/typeGuards'

import { DeleteViewAlertDialog } from './DeleteViewAlertDialog'
import { useCreateSavedViewMutation, useSavedViewsQuery } from './hooks'
import type { SavedView } from './hooks'
import type { DialogOpenProps } from './types'
import { validateViewName } from './validateViewName'

import styles from './SaveViewDialog.module.css'

type SaveViewDialogProps = DialogOpenProps

const FILTER_ERROR_ID = 'savedViewFilterError'
const NAME_ERROR_ID = 'savedViewNameError'

/**
 * Maximum saved views per user per organization. Enforced server-side as a
 * soft limit; the client mirrors it here to surface a preemptive limit state
 * computed from the cached list (never by parsing the server error message).
 */
const SAVED_VIEW_LIMIT = 25

/** How many least-recently-updated views the limit state offers to delete. */
const LIMIT_STATE_VIEW_COUNT = 5

/** Dialog for naming and saving the current route + filters as a new saved view. */
export function SaveViewDialog({
  open,
  onOpenChange,
}: SaveViewDialogProps): JSX.Element {
  const { pathname, search } = useLocation()
  const [createSavedView, { loading }] = useCreateSavedViewMutation()
  const { savedViews } = useSavedViewsQuery()

  const [name, setName] = useState('')
  const [identity, setIdentity] = useState<IconPickerValue>(
    DEFAULT_ICON_PICKER_VALUE,
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Set when the server rejects a create with LimitReachedError (the soft
  // limit can be raced from another tab even though the cached count is below
  // the limit). The dialog then shows the same limit state until a deletion
  // frees a slot.
  const [serverLimitReached, setServerLimitReached] = useState(false)
  const [viewPendingDelete, setViewPendingDelete] = useState<SavedView | null>(
    null,
  )

  // Capture lives in render (not in a submit-time effect) so we can preview
  // a filter-shape failure immediately when the dialog opens — the save
  // button stays clickable, but the alert region is already in the
  // accessibility tree before the user tabs to it.
  const captureResult = useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const section = deriveSectionFromRoute(pathname)
    const view = deriveViewFromRoute(pathname, searchParams)
    const result = captureFilters(section, pathname, searchParams)
    return { section, view, result }
  }, [pathname, search])

  // Reset local state whenever the dialog transitions to closed so a
  // subsequent open starts fresh.
  useEffect(() => {
    if (!open) {
      setName('')
      setIdentity(DEFAULT_ICON_PICKER_VALUE)
      setErrorMessage(null)
      setServerLimitReached(false)
      setViewPendingDelete(null)
    }
  }, [open])

  // Clear the server-race lock as soon as a deletion drops the count, so the
  // dialog unlocks the save form without the user having to reopen it.
  const previousCount = useRef(savedViews.length)
  useEffect(() => {
    if (savedViews.length < previousCount.current) {
      setServerLimitReached(false)
    }
    previousCount.current = savedViews.length
  }, [savedViews.length])

  const atLimit = serverLimitReached || savedViews.length >= SAVED_VIEW_LIMIT

  // Never show an under-count in the limit state: a soft-limit race
  // (`serverLimitReached`) can leave the cached list below the limit even
  // though the server treats it as full, so clamp the displayed count to at
  // least the limit to avoid contradictory copy like "3 of 25 views used".
  const usedCount = Math.max(savedViews.length, SAVED_VIEW_LIMIT)

  // The 5 least-recently-updated views, oldest first, offered for deletion.
  // updatedAt is an ISO timestamp, so a lexicographic compare sorts by time.
  const deletableViews = useMemo(
    () =>
      [...savedViews]
        .toSorted((a, b) => a.updatedAt.localeCompare(b.updatedAt))
        .slice(0, LIMIT_STATE_VIEW_COUNT),
    [savedViews],
  )

  const captureFailed = !captureResult.result.ok

  const describedBy =
    [
      errorMessage ? NAME_ERROR_ID : null,
      captureFailed ? FILTER_ERROR_ID : null,
    ]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()

    const error = validateViewName(name, { submitting: true })
    setErrorMessage(error)
    if (error) return

    // Capture must have succeeded — the alert region above the submit
    // button is already announcing the failure, so we just refuse to call
    // the mutation rather than re-deriving an error message here.
    if (!captureResult.result.ok) return

    const outcome = await createSavedView({
      name: name.trim(),
      section: captureResult.section,
      view: captureResult.view,
      filters: captureResult.result.filters,
      icon: identity.icon,
      color: identity.color,
    })

    if (outcome.success) {
      onOpenChange(false)
      return
    }

    // Soft-limit race: another tab used the last slot. Switch to the limit
    // state (the create hook intentionally does not toast this error type).
    if (isOfType(outcome.error, 'LimitReachedError')) {
      setServerLimitReached(true)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>Save Current View</Dialog.Title>
        </Dialog.Header>
        <VisuallyHidden>
          <Dialog.Description>
            Name this view so you can return to the current page and filters
            later from the sidebar.
          </Dialog.Description>
        </VisuallyHidden>
        <Dialog.Body>
          {atLimit ? (
            <Flex direction="column" gap="sm">
              <Notice variant="warning">
                <Notice.Heading>
                  {usedCount} of {SAVED_VIEW_LIMIT} views used
                </Notice.Heading>
                <Notice.Text>
                  To make room, delete one of your least-recently-updated views
                  below, or manage all your views from the sidebar.
                </Notice.Text>
              </Notice>
              <ul aria-label="Saved views" className={styles.limitList}>
                {deletableViews.map((view) => (
                  <Card asChild key={view.id} className={styles.limitRow}>
                    <li>
                      <Flex
                        direction="column"
                        gap="3xs"
                        className={styles.limitRowDetails}
                      >
                        <Text weight="bold" translate="no">
                          {view.name}
                        </Text>
                        <Text
                          as="time"
                          size="sm"
                          color="subtle"
                          dateTime={view.updatedAt}
                          data-testid="saved-view-last-updated"
                        >
                          Updated{' '}
                          {formatDistanceToNowStrict(new Date(view.updatedAt), {
                            addSuffix: true,
                          })}
                        </Text>
                      </Flex>
                      <IconButton
                        label={`Delete ${view.name}`}
                        tooltip="Delete view"
                        variant="critical-subtle"
                        onClick={(): void => setViewPendingDelete(view)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </li>
                  </Card>
                ))}
              </ul>
              <Flex justify="end" className={styles.formButtons} fullWidth>
                <Button
                  variant="tertiary"
                  size="large"
                  type="button"
                  onClick={(): void => onOpenChange(false)}
                >
                  Close
                </Button>
              </Flex>
            </Flex>
          ) : (
            <form
              onSubmit={(event): void => {
                void handleSubmit(event)
              }}
            >
              <Label htmlFor="savedViewName" className={styles.label}>
                Name
              </Label>
              <Flex gap="xs" align="center">
                <IconPicker value={identity} onValueChange={setIdentity} modal>
                  <IconPicker.Trigger />
                  <IconPicker.Content />
                </IconPicker>
                <Input
                  id="savedViewName"
                  className={styles.nameInput}
                  size="large"
                  value={name}
                  aria-required
                  aria-invalid={!!errorMessage}
                  aria-describedby={errorMessage ? NAME_ERROR_ID : undefined}
                  onChange={(event): void => {
                    const next = event.target.value
                    setName(next)
                    setErrorMessage(validateViewName(next))
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </Flex>
              {errorMessage && (
                <Text
                  id={NAME_ERROR_ID}
                  color="critical"
                  role="alert"
                  className={styles.errorLabel}
                >
                  {errorMessage}
                </Text>
              )}

              {captureFailed && (
                <Notice
                  id={FILTER_ERROR_ID}
                  variant="error"
                  role="alert"
                  className={styles.filterError}
                >
                  <Notice.Heading>
                    Some filters can&apos;t be saved
                  </Notice.Heading>
                  <Notice.Text>
                    The link you opened includes filters that aren&apos;t
                    supported here. Clear them and try again.
                  </Notice.Text>
                </Notice>
              )}

              <Flex
                gap="xs"
                justify="start"
                direction="row-reverse"
                className={styles.formButtons}
              >
                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  loading={loading}
                  aria-describedby={describedBy}
                  disabled={captureFailed}
                >
                  Save
                </Button>
                <Button
                  variant="tertiary"
                  size="large"
                  type="button"
                  onClick={(): void => onOpenChange(false)}
                >
                  Cancel
                </Button>
              </Flex>
            </form>
          )}
        </Dialog.Body>
      </Dialog.Content>
      {viewPendingDelete && (
        <DeleteViewAlertDialog
          view={viewPendingDelete}
          open
          onOpenChange={(isOpen): void => {
            if (!isOpen) setViewPendingDelete(null)
          }}
        />
      )}
    </Dialog>
  )
}
